<template>
  <BaseCenteredModal
    v-model:is-modal-open="localModalOpened"
    :title="upperFirst($t('global.validate'))"
    size="medium"
  >
    <template #body>
      <div class="flex w-full flex-col gap-2">
        <div v-if="loading" class="h-32">
          <LoadingComponent />
        </div>
        <div v-else>
          <InfoPanel
            v-if="invoice.doc_type !== 'QUO'"
            :title="
              upperFirst(
                $t(
                  'toasts.once_validated_an_invoicing_document_cannot_be_edited'
                )
              )
            "
            type="warning"
          />
          <div class="mt-2 grid grid-cols-2 gap-3">
            <div class="col-start-1">
              <SelectComponent
                v-model="formValues.account_diary_id"
                name="doc_role"
                :label="$t('attributes.accounting_diary')"
                :values="accountDiaries"
                required
              />
            </div>
            <SelectComponent
              v-model="formValues.doc_role"
              name="doc_role"
              :label="$t('attributes.doc_role')"
              :values="initialMetadata?.enums?.invoicing_doc_roles"
              required
            />
            <template v-if="formValues.account_diary_id">
              <div class="col-start-1">
                <BaseShowEditDatePicker
                  v-model:date="formValues.emission_date"
                  :label="$t('attributes.emission_date')"
                  :edition-mode="true"
                  name="emission_date"
                  :errors="errors.emission_date"
                  :min-date="emissionDateFrom"
                  :max-date="emissionDateTo"
                  required
                />
              </div>
              <div>
                <BaseShowEditDatePicker
                  v-model:date="formValues.due_date"
                  :label="$t('attributes.due_date')"
                  :edition-mode="true"
                  name="due_date"
                  :errors="errors.due_date"
                  :min-date="formValues.emission_date"
                  required
                />
              </div>
            </template>
          </div>
        </div>
        <div>
          <div class="grid w-full grid-cols-1 gap-3 md:grid-cols-2">
            <div class="col-start-1">
              <SelectInvoiceStatuses
                v-model="formValues.status"
                :label="$t('attributes.status')"
                :errors="errors?.status"
                edition-mode
                required
              />
              <span class="form-help ml-4 text-yellow-600">
                <FontAwesomeIcon :icon="['fal', 'triangle-exclamation']" />
                {{ upperFirst($t('global.can_update_invoice_status')) }}
              </span>
            </div>
            <div
              v-if="
                invoice.dossier_id &&
                invoice.doc_role === 'ff' &&
                formValues.status === 'PAI'
              "
              class="mt-2"
            >
              <div class="flex w-full justify-between">
                <h2 class="text-xs font-medium capitalize-first">
                  {{ $t('global.change_dossier_status') }}
                </h2>
                <div>
                  <BaseShowEditRadio
                    v-model="formValues.change_status_dossier"
                    class="col-span-1"
                    :edition-mode="true"
                    size="extra-small"
                    name="change_status_dossier"
                    :options="[
                      { id: 1, name: $t('enums.yesNo.1') },
                      { id: 0, name: $t('enums.yesNo.0') }
                    ]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <BaseButton
        :disabled="!formValid || isSubmitting"
        color="primary"
        :title="$t('button.save')"
        data-faro-user-action-name="invoice.establish"
        @click="validateInvoice"
      />
      <BaseButton
        color="secondary"
        :title="$t('button.cancel')"
        @click="() => (localModalOpened = false)"
      />
    </template>
  </BaseCenteredModal>
  <AvailableLocationStockModal
    v-model:is-modal-open="chooseStockFromLocationModal"
    v-model:original-request="originalRequest"
    v-model:new-orders="newOrders"
    :locations="locations"
    :supplier-with-orders="supplierOrderActive"
    :update-item="false"
    @selected-items="handleSelectedItem"
  />
</template>

<script>
import { defaultEmissionDate } from './defaultEmissionDate'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import BaseShowEditDatePicker from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDatePickerv2.vue'
import { mapActions, mapGetters } from 'vuex'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import moment from 'moment'
import InfoPanel from '@ui/InfoPanel.vue'
import SelectInvoiceStatuses from '@c/InitialStateFormComponent/Enums/SelectInvoiceStatuses.vue'
import BaseShowEditRadio from '@c/addf-package/components/BaseShowEditRadioGroup/BaseShowEditRadio.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { upperFirst } from 'lodash-es'
import AvailableLocationStockModal from '@c/Stock/AvailableLocationStockModal.vue'

export default {
  name: 'ValidateModal',
  components: {
    AvailableLocationStockModal,
    FontAwesomeIcon,
    BaseShowEditRadio,
    SelectInvoiceStatuses,
    InfoPanel,
    BaseButton,
    BaseShowEditDatePicker,
    SelectComponent,
    LoadingComponent,
    BaseCenteredModal
  },
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
      required: true
    }
  },
  emits: ['update:modalOpened'],
  data() {
    return {
      isOpen: false,
      accountDiaries: null,
      loading: false,
      formValues: {
        account_diary_id: null,
        emission_date: '',
        due_date: '',
        doc_role: null,
        status: 'EST',
        change_status_dossier: false
      },
      chooseStockFromLocationModal: false,
      locations: [],
      newOrders: [],
      originalRequest: null,
      supplierOrderActive: [],
      errors: {},
      isSubmitting: false
    }
  },
  computed: {
    ...mapGetters({
      invoice: 'invoicing/getInvoice',
      initialMetadata: 'metadata/getInitialMetadata'
    }),
    localModalOpened: {
      get() {
        return this.modalOpened
      },
      set(value) {
        this.$emit('update:modalOpened', value)
      }
    },
    selectedAccountingDiary() {
      return (
        this.accountDiaries?.find(
          (accountDiary) => accountDiary.id === this.formValues.account_diary_id
        ) ?? null
      )
    },
    formValid() {
      return (
        this.formValues.account_diary_id?.length &&
        this.formValues.emission_date?.length &&
        this.formValues.due_date?.length &&
        this.formValues.doc_role
      )
    },
    emissionDateFrom() {
      if (this.selectedAccountingDiary) {
        return (
          this.selectedAccountingDiary.last_invoice_date ??
          this.selectedAccountingDiary.selectedAccountingDiary?.from
        )
      }
      return undefined
    },
    emissionDateTo() {
      if (this.selectedAccountingDiary) {
        return this.selectedAccountingDiary?.to
        // let today = new Date()
        // console.log('emissionDateTo::', this.selectedAccountingDiary.to)
        // let account_diary_end_date = new Date(this.selectedAccountingDiary.to)
        // console.log(account_diary_end_date)
        // if (account_diary_end_date < today) {
        //   return this.selectedAccountingDiary?.to
        // } else {
        //   return today
        // }
      }
      return undefined
    }
  },
  watch: {
    modalOpened(newValue, _oldValue) {
      if (newValue) {
        this.formValues.doc_role = this.invoice.doc_role
        this.formValues.account_diary_id = this.invoice.account_diary_id
        // if (!this.accountDiaries) {
        this.loading = true
        this.processFetchInvoiceEstablishMetadataAction({
          invoice_id: this.invoice.id,
          doc_role: this.invoice.doc_role
        }).then((response) => {
          this.accountDiaries = response.account_diaries
          if (this.accountDiaries.length && !this.formValues.account_diary_id) {
            this.formValues.account_diary_id = this.accountDiaries[0].id
          }
          this.changeEmissionDateBaseOnAccountDiaryId()
          this.loading = false
        })
        // }
      } else {
        this.accountDiaries = null
        this.formValues.emission_date = ''
        this.formValues.due_date = ''
        this.formValues.doc_role = null
      }
    },
    'formValues.account_diary_id'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.formValues.emission_date = ''
        this.formValues.due_date = ''
        this.changeEmissionDateBaseOnAccountDiaryId()
      }
    },
    'formValues.emission_date'(newValue, _oldValue) {
      this.setDueDate()
    },
    'formValues.doc_role'(newValue, oldValue) {
      if (oldValue && newValue !== oldValue) {
        this.loading = true
        this.formValues.account_diary_id = null
        this.processFetchInvoiceEstablishMetadataAction({
          invoice_id: this.invoice.id,
          doc_role: this.formValues.doc_role
        }).then((response) => {
          this.accountDiaries = response.account_diaries
          if (this.accountDiaries.length) {
            this.formValues.account_diary_id = this.accountDiaries[0].id
          }
          this.changeEmissionDateBaseOnAccountDiaryId()
          this.loading = false
        })
      }
    }
  },
  methods: {
    upperFirst,
    ...mapActions({
      processFetchInvoiceEstablishMetadataAction:
        'invoicing/fetchInvoiceEstablishMetadata',
      processSetInvoiceEstablishAction: 'invoicing/setInvoiceEstablish',
      processManageStockInvoiceAction: 'invoicing/manageStockInvoice'
    }),
    validateInvoice() {
      if (this.isSubmitting) return

      this.isSubmitting = true

      this.processSetInvoiceEstablishAction({
        ...this.formValues,
        invoice_id: this.invoice.id
      })
        .then((response) => {
          if (response.original) {
            this.originalRequest = response.original
            this.locations = response.locations
            this.supplierOrderActive = response.suppliers
            this.convert_dossier_id = response.dossier_id
            this.chooseStockFromLocationModal = true
          } else {
            this.localModalOpened = false
            this.$h.toastSuccess(this.$t('global.saved'))
          }
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },
    changeEmissionDateBaseOnAccountDiaryId() {
      let accountDiary = this.selectedAccountingDiary
      if (!accountDiary || this.formValues.emission_date.length) {
        return
      }
      this.formValues.emission_date = defaultEmissionDate(accountDiary)
      this.setDueDate()
    },
    setDueDate() {
      if (
        !this.formValues.emission_date ||
        this.formValues.emission_date?.length === 0
      ) {
        this.formValues.due_date = ''
      } else {
        this.formValues.due_date = moment(
          this.formValues.emission_date,
          'YYYY-MM-DD'
        )
          .add(this.selectedAccountingDiary.payment_period ?? 30, 'days')
          .format('YYYY-MM-DD')
      }
    },
    handleSelectedItem() {
      this.processManageStockInvoiceAction({
        id: this.invoice.id,
        data: {
          original: this.originalRequest
        }
      }).then((response) => {
        this.localModalOpened = false
        this.chooseStockFromLocationModal = false
      })
    }
  }
}
</script>
